import React from "react"
import * as A from "fp-ts/Array"
import { Icon } from "../../../components"
import { PropertyDetails } from "../../Model/overviewModel"
import { GDRIVE_BASE_URL } from "../../../runtimeVariables"

export default function ({
  address,
  images,
  slide,
  updateModalContents,
}: {
  address: string
  images: string[]
  slide: number
  updateModalContents: (contents: PropertyDetails) => void
}): JSX.Element {
  const carouselRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    // Listen to update index on (manual) scroll
    const onscroll = () => {
      if (!carouselRef.current) return
      const { width } = carouselRef.current.getBoundingClientRect()
      const scrollLeft = carouselRef.current.scrollLeft
      updateModalContents({
        address,
        images,
        slide: slideIndex(scrollLeft, width),
      })
    }

    carouselRef.current?.addEventListener("scroll", onscroll)

    return () => carouselRef.current?.removeEventListener("scroll", onscroll)
  }, [])

  React.useEffect(() => {
    // CLick on img navigation
    const onclick = ({ offsetX }: MouseEvent) => {
      if (!carouselRef.current) return
      const { width } = carouselRef.current.getBoundingClientRect()
      scrollCarouselBy({
        carouselRef,
        slidesLength: images.length,
        direction: offsetX / width > 0.5 ? "right" : "left",
      })
    }
    carouselRef.current?.addEventListener("click", onclick)
    return () => carouselRef.current?.removeEventListener("click", onclick)
  }, [])

  return A.isNonEmpty(images) ? (
    <div className=" relative gridcontainer p-0 gap-x-4">
      <button
        onClick={() =>
          scrollCarouselBy({
            carouselRef,
            slidesLength: images.length,
            direction: "left",
          })
        }
        className={`hidden lg:block lg:col-start-1 lg:col-end-2 rotate-180 ${
          slide === 0 ? "opacity-10" : ""
        } `}
      >
        <span className=" inline-block w-full w-">
          <Icon.ChevronLarge />
        </span>
      </button>
      <div
        ref={carouselRef}
        className="my-10 lg:my-16 lg:col-start-2 lg:col-end-16 img-carousel cursor-pointer aspect-[3/2]"
      >
        {images.map((url, index) => (
          <div
            key={index}
            className="relative flex-grow-0 flex-shrink-0 basis-full"
          >
            <img
              className=" w-full h-full object-cover"
              src={`${GDRIVE_BASE_URL}/${url}`}
              alt="text"
              referrerPolicy="no-referrer"
            />
          </div>
        ))}
      </div>
      <button
        onClick={() =>
          scrollCarouselBy({
            carouselRef,
            slidesLength: images.length,
            direction: "right",
          })
        }
        className={`hidden lg:block lg:col-start-16 lg:col-end-17 ${
          slide === images.length - 1 ? "opacity-10" : ""
        }`}
      >
        <span className="inline-block w-full">
          <Icon.ChevronLarge />
        </span>
      </button>
    </div>
  ) : (
    <></>
  )
}

const slideIndex = (scroll: number, width: number): number =>
  Math.round(scroll / width)

const scrollCarouselBy = ({
  carouselRef,
  direction,
  slidesLength,
}: {
  carouselRef: React.RefObject<HTMLDivElement>
  direction: "right" | "left"
  slidesLength: number
}): void => {
  if (!carouselRef.current) return
  const { width } = carouselRef.current.getBoundingClientRect()
  const index = slideIndex(carouselRef.current.scrollLeft, width)
  const offset = slidesLength - 1

  const scrollLeft = () => {
    if (index === 0 && direction === "left") return width * offset
    if (index === offset && direction === "right") return -width * offset
    return width * (direction === "right" ? 1 : -1)
  }

  carouselRef.current.scrollBy({
    left: scrollLeft(),
    behavior: "smooth",
  })

  return
}
